import { Routes, Route, Navigate } from 'react-router-dom';
import { PerformanceDock } from '@/components/design/PerformanceDock';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { SkipLink } from '@/components/SkipLink';
import { DashboardPage } from '@/app/DashboardPage';
import { TrainPage } from '@/app/TrainPage';
import { ProgressPage } from '@/app/ProgressPage';
import { DrillsPage } from '@/app/DrillsPage';
import { DrillDetailPage } from '@/app/DrillDetailPage';
import { ProfilePage } from '@/features/profile/ProfilePage';
import { AccountPage } from '@/features/profile/AccountPage';
import { TrainingHubPage } from '@/features/training/TrainingHubPage';
import { ProfileSelectPage } from '@/features/onboarding/ProfileSelectPage';
import { CreateProfilePage } from '@/features/onboarding/CreateProfilePage';
import { SocialFeedPage } from '@/features/social/SocialFeedPage';
import { MatchLogPage } from '@/features/matches/MatchLogPage';
import { MatchFormPage } from '@/features/matches/MatchFormPage';
import { MatchDetailPage } from '@/features/matches/MatchDetailPage';
import { ReviewPage } from '@/app/ReviewPage';
import { SettingsPage } from '@/app/SettingsPage';
import { ReloadPrompt } from '@/components/ReloadPrompt';
import { OnboardingWizard } from '@/features/onboarding/OnboardingWizard';

import { useEffect } from 'react';
import { useAthleteStore, useSettingsStore } from '@/store';
import { useOnboardingStore } from '@/store/onboardingStore';
import { useAudioInitialization } from '@/hooks/useAudioInitialization';
import { seedDatabase } from '@/lib/db/seed';

import { AuthPage } from '@/features/auth/AuthPage';
import { useAuthStore } from '@/store/authStore';
import { NutritionPage } from '@/features/nutrition/NutritionPage';
import { VideoAnalysisPage } from '@/features/analysis/VideoAnalysisPage';
import { StanceMotionPage } from '@/features/training/StanceMotionPage';
import { PricingPage } from '@/features/premium/PricingPage';
import { AnalyticsPage } from '@/features/analytics/AnalyticsPage';

import { TeamDashboard } from '@/features/coach/TeamDashboard';
import { AthleteListPage } from '@/features/parent/AthleteListPage';
import { InviteAcceptPage } from '@/features/coach/InviteAcceptPage';
import { SharePage } from '@/features/parent/SharePage';
import { VisibilityPage } from '@/features/parent/VisibilityPage';
import { PublicProfilePage } from '@/features/public/PublicProfilePage';

export function App() {
    useAudioInitialization();
    const hydrateAthletes = useAthleteStore(s => s.hydrate);
    const hydrateSettings = useSettingsStore(s => s.hydrate);
    const hydrated = useAthleteStore(s => s.hydrated);

    const hydrateAuth = useAuthStore(s => s.hydrate);
    const authLoading = useAuthStore(s => s.loading);
    const { account, isGuest, isParent, isCoach } = useAuthStore();

    // Onboarding state
    const { completed, loaded: onboardingLoaded, hydrate: hydrateOnboarding } = useOnboardingStore();
    const athleteId = useAthleteStore((s) => s.activeId);

    useEffect(() => {
        seedDatabase().catch(console.error);
        hydrateAuth();
        hydrateSettings();
    }, [hydrateAuth, hydrateSettings]);

    useEffect(() => {
        hydrateAthletes(account?.id);
    }, [account?.id, hydrateAthletes]);

    useEffect(() => {
        hydrateOnboarding(athleteId);
    }, [athleteId, hydrateOnboarding]);

    if (!hydrated || !onboardingLoaded || authLoading) {
        return (
            <div className="min-h-screen bg-bg flex flex-col items-center justify-center p-4">
                <div className="w-12 h-12 border-4 border-white/10 border-t-gold-base rounded-full animate-spin mb-4" />
                <p className="text-text-muted text-sm font-medium uppercase tracking-widest animate-pulse">Initializing</p>
            </div>
        );
    }

    // Auth Guard
    if (!account && !isGuest) {
        return <AuthPage />;
    }

    if (!completed) {
        return <OnboardingWizard />;
    }

    return (
        <ErrorBoundary>
            <SkipLink />
            <div className="min-h-screen bg-bg">
                <main id="main-content" className="max-w-lg mx-auto px-4 pb-32">
                    <Routes>

                        {/* Role-based root routing */}
                        <Route path="/" element={
                            isCoach ? <Navigate to="/team" replace /> :
                                isParent ? <Navigate to="/athletes" replace /> :
                                    <DashboardPage />
                        } />

                        {/* Role specific routes */}
                        <Route path="/team" element={<TeamDashboard />} />
                        <Route path="/athletes" element={<AthleteListPage />} />
                        <Route path="/athletes/:id/share" element={<SharePage />} />
                        <Route path="/athletes/:id/visibility" element={<VisibilityPage />} />
                        <Route path="/invite" element={<InviteAcceptPage />} />
                        <Route path="/public/:athleteId" element={<PublicProfilePage />} />

                        {/* Common or legacy athlete routes */}
                        <Route path="/train" element={<TrainingHubPage />} />
                        <Route path="/train/session" element={<TrainPage />} />
                        <Route path="/train/stance-motion" element={<StanceMotionPage />} />
                        <Route path="/pricing" element={<PricingPage />} />
                        <Route path="/drills" element={<DrillsPage />} />
                        <Route path="/drills/:id" element={<DrillDetailPage />} />
                        <Route path="/progress" element={<ProgressPage />} />
                        <Route path="/profile" element={<ProfilePage />} />
                        <Route path="/profile/edit" element={<AccountPage />} />
                        <Route path="/social" element={<SocialFeedPage />} />
                        <Route path="/matches" element={<MatchLogPage />} />
                        <Route path="/matches/new" element={<MatchFormPage />} />
                        <Route path="/matches/:id" element={<MatchDetailPage />} />
                        <Route path="/matches/:id/edit" element={<MatchFormPage />} />
                        <Route path="/profiles" element={<ProfileSelectPage />} />
                        <Route path="/profiles/create" element={<CreateProfilePage />} />
                        <Route path="/review" element={<ReviewPage />} />
                        <Route path="/nutrition" element={<NutritionPage />} />
                        <Route path="/analysis" element={<VideoAnalysisPage />} />
                        <Route path="/settings" element={<SettingsPage />} />
                        <Route path="/analytics" element={<AnalyticsPage />} />
                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                </main>
                <PerformanceDock />
                <ReloadPrompt />
            </div>
        </ErrorBoundary>
    );
}
